import { FC, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    CircularProgress,
    Alert,
    Grid,
    TextField,
    Card,
    CardContent,
    Typography,
    LinearProgress,
} from '@mui/material';
import { Search, FilterList, Clear } from '@mui/icons-material';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIButton } from '../../atomic/atoms/SCUIButton/SCUIButton';
import { SCUITabs } from '../../atomic/atoms/SCUITabs/SCUITabs';
import AIStudioAnalyticsQuery from '../../services/query/AIStudioAnalyticsQuery';
import { StatisticsCards } from './components/StatisticsCards';
import { ProductionTrendChart } from './components/ProductionTrendChart';
import { UserComparisonChart } from './components/UserComparisonChart';
import { StatusDistributionChart } from './components/StatusDistributionChart';
import { UserDetailTable } from './components/UserDetailTable';
import { FilterDateField } from './components/FilterDateField';
import TimeHelper from '../../utils/time';
import './AIStudioAnalytics.scss';

// Özet (summary) verileri - filtre uygulanmaz
interface SummaryData {
    todayProductionCount: number;
    monthlyProductionCount: number;
    topProducer: {
        username: string;
        totalProductions: number;
    } | null;
    productionTrend: Array<{
        date: string;
        productionCount: number;
        successfulCount: number;
        failedCount: number;
    }>;
}

// Filtrelenebilir veriler
interface FilteredData {
    allUserStats: Array<{
        username: string;
        totalProductions: number;
        successfulProductions: number;
        failedProductions: number;
        successRate: number;
        inpaintCount: number;
        uniqueBarcodeCount: number;
        efficiency: number;
        generationTypeBreakdown: Array<{
            generationTypeCode: string;
            count: number;
        }>;
    }>;
    statusDistribution: Array<{
        status: string;
        count: number;
        percentage: number;
    }>;
    topUsers: Array<{
        username: string;
        totalProductions: number;
    }>;
}

const TAB_OVERVIEW = 1;
const TAB_USER_DETAILS = 2;

export const AIStudioAnalytics: FC = () => {
    const { t } = useTranslation();

    const todayDate = TimeHelper.getQuickDateRange('today').startDate;

    const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);

    // Summary data state
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryError, setSummaryError] = useState<string | null>(null);
    const [summaryData, setSummaryData] = useState<SummaryData | null>(null);

    // Filtered data state
    const [filteredLoading, setFilteredLoading] = useState(true);
    const [filteredError, setFilteredError] = useState<string | null>(null);
    const [filteredData, setFilteredData] = useState<FilteredData | null>(null);

    // Filter state - default olarak bugünün tarihi
    const [filterUsername, setFilterUsername] = useState('');
    const [filterStartDate, setFilterStartDate] = useState(todayDate);
    const [filterEndDate, setFilterEndDate] = useState(todayDate);
    const [filterProductCode, setFilterProductCode] = useState('');

    // Applied filter state - default olarak bugünün tarihi ile başla
    const [appliedFilter, setAppliedFilter] = useState<{
        username: string;
        startDate: string;
        endDate: string;
        productCode: string;
    }>({ username: '', startDate: todayDate, endDate: todayDate, productCode: '' });

    const tabList = [
        { id: TAB_OVERVIEW, name: t('aiStudioAnalytics.tabs.overview') },
        { id: TAB_USER_DETAILS, name: t('aiStudioAnalytics.tabs.userDetails') },
    ];

    // Özet verileri yükle (bir kere, sayfa açılınca)
    useEffect(() => {
        loadSummary();
    }, []);

    // Filtrelenmiş verileri yükle (filtre değişince)
    useEffect(() => {
        loadFilteredData();
    }, [appliedFilter]);

    const loadSummary = async () => {
        try {
            setSummaryLoading(true);
            setSummaryError(null);
            const response = await AIStudioAnalyticsQuery.GetAIStudioAnalytics();
            setSummaryData(response);
        } catch (err) {
            console.error('Error loading summary:', err);
            setSummaryError(t('aiStudioAnalytics.error'));
        } finally {
            setSummaryLoading(false);
        }
    };

    const loadFilteredData = useCallback(async () => {
        try {
            setFilteredLoading(true);
            setFilteredError(null);

            const params: { username?: string; startDate?: string; endDate?: string; productCode?: string } = {};
            if (appliedFilter.username) params.username = appliedFilter.username;
            if (appliedFilter.startDate) params.startDate = appliedFilter.startDate;
            if (appliedFilter.endDate) params.endDate = appliedFilter.endDate;
            if (appliedFilter.productCode) params.productCode = appliedFilter.productCode;

            const response = await AIStudioAnalyticsQuery.GetFilteredStats(
                Object.keys(params).length > 0 ? params : undefined
            );
            setFilteredData(response);
        } catch (err) {
            console.error('Error loading filtered data:', err);
            setFilteredError(t('aiStudioAnalytics.error'));
        } finally {
            setFilteredLoading(false);
        }
    }, [appliedFilter, t]);

    const handleApplyFilter = () => {
        setAppliedFilter({
            username: filterUsername.trim(),
            startDate: filterStartDate,
            endDate: filterEndDate,
            productCode: filterProductCode.trim(),
        });
    };

    const handleClearFilter = () => {
        setFilterUsername('');
        setFilterStartDate(todayDate);
        setFilterEndDate(todayDate);
        setFilterProductCode('');
        setAppliedFilter({ username: '', startDate: todayDate, endDate: todayDate, productCode: '' });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleApplyFilter();
        }
    };

    const handleTabChange = (_event: unknown, tabIndex: number) => {
        setActiveTab(tabIndex);
    };

    const renderFilterBar = () => (
        <Card elevation={0} sx={{ mb: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: '24px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                    <FilterList sx={{ color: 'text.primary' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {t('aiStudioAnalytics.filter.title')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                    <TextField
                        size="small"
                        label={t('aiStudioAnalytics.filter.username')}
                        placeholder={t('aiStudioAnalytics.filter.usernamePlaceholder')}
                        value={filterUsername}
                        onChange={(e) => setFilterUsername(e.target.value)}
                        onKeyDown={handleKeyDown}
                        sx={{ 
                            minWidth: 200,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            }
                        }}
                        slotProps={{
                            input: {
                                startAdornment: <Search sx={{ mr: 0.5, color: 'action.disabled' }} />,
                            },
                        }}
                    />
                    <TextField
                        size="small"
                        label={t('aiStudioAnalytics.filter.productCode')}
                        placeholder={t('aiStudioAnalytics.filter.productCodePlaceholder')}
                        value={filterProductCode}
                        onChange={(e) => setFilterProductCode(e.target.value)}
                        onKeyDown={handleKeyDown}
                        sx={{ 
                            minWidth: 200,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            }
                        }}
                        slotProps={{
                            input: {
                                startAdornment: <Search sx={{ mr: 0.5, color: 'action.disabled' }} />,
                            },
                        }}
                    />
                    <FilterDateField
                        label={t('aiStudioAnalytics.filter.startDate')}
                        value={filterStartDate}
                        onChange={setFilterStartDate}
                        onKeyDown={handleKeyDown}
                    />
                    <FilterDateField
                        label={t('aiStudioAnalytics.filter.endDate')}
                        value={filterEndDate}
                        onChange={setFilterEndDate}
                        onKeyDown={handleKeyDown}
                    />
                    <SCUIButton
                        text={t('aiStudioAnalytics.filter.apply')}
                        type="primary"
                        size="m"
                        sizeType="fixed"
                        onClick={handleApplyFilter}
                        startIcon={<Search />}
                    />
                    <SCUIButton
                        text={t('aiStudioAnalytics.filter.clear')}
                        type="secondary"
                        size="m"
                        sizeType="fixed"
                        onClick={handleClearFilter}
                        startIcon={<Clear />}
                    />
                </Box>
            </CardContent>
        </Card>
    );

    const renderFilteredContent = () => {
        if (filteredError) {
            return (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {filteredError}
                </Alert>
            );
        }

        if (filteredLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!filteredData) {
            return null;
        }

        if (activeTab === TAB_OVERVIEW) {
            return (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <UserComparisonChart data={filteredData.topUsers} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <StatusDistributionChart data={filteredData.statusDistribution} />
                    </Grid>
                </Grid>
            );
        }

        return (
            <Box>
                <UserDetailTable
                    data={filteredData.allUserStats}
                    startDate={appliedFilter.startDate}
                    endDate={appliedFilter.endDate}
                    productCode={appliedFilter.productCode}
                />
            </Box>
        );
    };

    const isAnyLoading = summaryLoading || filteredLoading;

    return (
        <SCUIContent headerName={t('aiStudioAnalytics.title')}>
            {isAnyLoading && <LinearProgress sx={{ mb: 2 }} />}

            <Box className="ai-studio-analytics" sx={{ pt: { xs: 2, sm: 3, md: 4 } }}>
                <SCUITabs
                    tabList={tabList}
                    activeTabIndex={activeTab}
                    onChange={handleTabChange}
                    iconShow={false}
                />

                {activeTab === TAB_OVERVIEW && (
                    <>
                        {summaryLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: 200,
                                    mb: 3,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : summaryError ? (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {summaryError}
                            </Alert>
                        ) : summaryData ? (
                            <>
                                <StatisticsCards
                                    todayProduction={summaryData.todayProductionCount}
                                    monthlyProduction={summaryData.monthlyProductionCount}
                                    topUser={summaryData.topProducer}
                                />

                                <Grid container spacing={3} sx={{ mb: 3 }}>
                                    <Grid size={{ xs: 12 }}>
                                        <ProductionTrendChart data={summaryData.productionTrend} />
                                    </Grid>
                                </Grid>
                            </>
                        ) : null}

                        {renderFilterBar()}
                        {renderFilteredContent()}
                    </>
                )}

                {activeTab === TAB_USER_DETAILS && (
                    <>
                        {renderFilterBar()}
                        {renderFilteredContent()}
                    </>
                )}
            </Box>
        </SCUIContent>
    );
};
